import { useMemo } from 'react';
import type {
  Policy,
  PolicyLimitationBucketUsage,
  PolicyDestinationAddressEntry,
  PolicyTokenTransferCondition,
} from '@mls-wallet/shared-types';
import { Separator } from '@/components/ui/separator';
import { useAddressBookEntriesByAddresses } from '@/hooks/organization/use-address-book-entries-by-addresses';
import { toHumanReadableTokenAmount } from '@/utils/tokenAmountUtils';
import {
  Section,
  SectionLeftColumn,
  SectionRightColumn,
  SectionRow,
  SectionTitle,
  SectionDescription,
} from '@/components/shared/SectionComponents';
import {
  getPolicyTypeLabel,
  getTransactionTypeLabel,
  formatAddress,
} from '../utils';
import { getNetworkDisplayName } from '@mls-wallet/shared-types';
import { formatName } from '@/lib/name';
import { TokenLogo } from '@/components/shared/token-logo';
import { ContractNameAddress } from '@/components/shared/contract-name-address';
import { AddressDisplay } from '@/components/shared/address-display';
import { TokenTransferLimit } from '@/components/shared/token-transfer-limit';
import { formatLocalizedDateTimeWithShortTz } from '@/lib/formatLocalizedDateTimeWithShortTz';
import { formatIntervalReset } from './limitation-selector-helpers';

interface PolicyDisplayFormProps {
  policy: Policy;
}

// ReadOnlyValue component for displaying values in the right column
const ReadOnlyValue = ({ children }: { children: React.ReactNode }) => (
  <div className="min-w-0 text-sm font-medium min-h-[40px] flex items-center">
    {children}
  </div>
);

function buildDestinationEntryMap(
  entries: PolicyDestinationAddressEntry[] | undefined
) {
  return new Map(
    (entries ?? []).map(
      (entry) => [entry.address.toLowerCase(), entry] as const
    )
  );
}

/** Matches EVM address shape for short display fallback in bucket keys. */
function looksLikeEvmAddress(value: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(value);
}

// Helper function to render addresses in a readable format
const renderAddresses = (
  condition: PolicyTokenTransferCondition | null,
  networkId: Policy['networkId']
) => {
  const addresses = condition?.destinationAddresses ?? [];
  if (addresses.length === 0)
    return <span className="text-muted-foreground">Any</span>;

  const destinationEntryByAddress = buildDestinationEntryMap(
    condition?.destinationAddressEntries
  );

  return (
    <div className="flex flex-wrap gap-2">
      {addresses.map((address) => {
        const entry = destinationEntryByAddress.get(address.toLowerCase());

        return (
          <div
            key={address}
            className="min-w-0 max-w-full rounded-full border border-white bg-zinc-100 px-2 py-0.5"
          >
            <AddressDisplay
              address={address}
              name={entry?.name ?? null}
              networkId={networkId}
              showAddressName
              addressNameLayout="inline"
              addressNameClassName="text-xs"
              showCopy
              showBlockExplorerLink
            />
          </div>
        );
      })}
    </div>
  );
};

// Helper to display account (simpler than OriginDisplay)
const AccountDisplay = ({
  account,
  networkId,
}: {
  account: Policy['accounts'][number];
  networkId: Policy['networkId'];
}) => (
  <div className="flex flex-col gap-1">
    <span className="font-medium">{account.name}</span>
    {account.address && (
      <span className="text-xs text-muted-foreground">
        <span className="capitalize">{getNetworkDisplayName(networkId)}</span>
        {' · '}
        <span className="font-mono">{formatAddress(account.address)}</span>
      </span>
    )}
  </div>
);

export function PolicyDisplayForm({ policy }: PolicyDisplayFormProps) {
  const contractInteractionCondition =
    policy.transactionType === 'CONTRACT_INTERACTION' &&
    'contractInteractionCondition' in policy
      ? policy.contractInteractionCondition
      : null;

  const contractAddresses = useMemo(() => {
    return (
      contractInteractionCondition?.map(
        (contract) => contract.contractAddress
      ) ?? []
    );
  }, [contractInteractionCondition]);

  // Policy payloads do not yet embed contract address-book labels, so resolve
  // exactly the referenced contract rows without scanning the full address book.
  const { entryByAddress: contractEntryByAddress } =
    useAddressBookEntriesByAddresses(contractAddresses);

  const accountById = useMemo(
    () => new Map(policy.accounts.map((account) => [account.id, account])),
    [policy.accounts]
  );
  const destinationEntryByAddress = useMemo(
    () =>
      buildDestinationEntryMap(
        policy.transactionType === 'TOKEN_TRANSFER'
          ? policy.tokenTransferCondition?.destinationAddressEntries
          : undefined
      ),
    [policy]
  );
  // Case: Show only buckets with usedAmountRaw > 0; the policy payload still carries the full set.
  const nonZeroLimitationBuckets =
    policy.limitationUsage?.buckets.filter(
      (bucket) => BigInt(bucket.usedAmountRaw) > 0n
    ) ?? [];

  /**
   * Renders a read-only summary of the policy's limitation configuration.
   * Returns a "Single transaction" placeholder when no limitation is set.
   */
  const renderLimitation = (limitation: Policy['limitation']) => {
    if (!limitation) {
      return <span className="text-muted-foreground">Single transaction</span>;
    }

    const details = [
      formatIntervalReset(limitation.hours),
      limitation.initiatorScope === 'PER_ITEM'
        ? 'Per initiator'
        : 'Across all initiators',
      limitation.sourceAccountScope === 'PER_ITEM'
        ? 'Per source account'
        : 'Across all source accounts',
      limitation.destinationScope === 'PER_ITEM'
        ? 'Per destination'
        : 'Across all destinations',
    ];

    return (
      <div className="flex flex-col gap-1">
        <span>Time interval</span>
        {details.map((detail) => (
          <span key={detail} className="text-xs text-muted-foreground">
            {detail}
          </span>
        ))}
      </div>
    );
  };

  /**
   * Renders a single initiator/source/destination dimension label for a usage
   * bucket row.
   *
   * Resolution priority for each kind:
   * - `initiator`: enriched user info from the bucket → address fallback
   * - `source`: enriched account info from bucket or `accountById` → address fallback
   * - `destination`: enriched entry from bucket or `destinationEntryByAddress` → shortened address
   *
   * `ALL` sentinel values are translated to human-readable "across all …" text
   * instead of displaying the raw sentinel string.
   */
  const renderBucketDimension = (
    label: string,
    value: string,
    kind: 'initiator' | 'source' | 'destination',
    bucket: PolicyLimitationBucketUsage
  ) => {
    // Case: value === 'ALL' — show an explicit "all ..." label instead of the raw sentinel.
    if (value === 'ALL') {
      const allLabel =
        kind === 'initiator'
          ? 'all initiators'
          : kind === 'source'
            ? 'all source accounts'
            : 'all destinations';
      return (
        <div>
          {label}: <span className="font-medium">{allLabel}</span>
        </div>
      );
    }

    if (kind === 'initiator') {
      const initiator = bucket.initiator;
      const primary =
        initiator &&
        (initiator.name?.trim() ? formatName(initiator.name) : undefined);
      return (
        <div>
          {label}:{' '}
          <span
            className={`font-medium ${
              primary ? 'text-foreground' : 'text-red-500'
            }`}
          >
            {primary ?? 'Failed to load initiator'}
          </span>
        </div>
      );
    }

    if (kind === 'source') {
      // Case: bucket.sourceAccount may be missing; resolve from accountById by this row's account id.
      const account = bucket.sourceAccount ?? accountById.get(value);
      const primary =
        account &&
        (account.name?.trim()
          ? formatName(account.name)
          : account.address
            ? formatAddress(account.address)
            : undefined);
      const fallback = looksLikeEvmAddress(value)
        ? formatAddress(value)
        : value;
      return (
        <div>
          {label}:{' '}
          <span className="font-medium text-foreground">
            {primary ?? fallback}
          </span>
        </div>
      );
    }

    if (kind === 'destination') {
      const entry =
        bucket.destination ??
        destinationEntryByAddress.get(value.toLowerCase());
      const trimmedName = entry?.name?.trim();
      const primary = trimmedName ?? formatAddress(entry?.address ?? value);
      return (
        <div>
          {label}:{' '}
          <span className="font-medium text-foreground">{primary}</span>
        </div>
      );
    }

    return (
      <div>
        {label}: <span className="font-mono">{value}</span>
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto w-full flex flex-col gap-8">
      {/* Network */}
      <Section title="Blockchain Network">
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Blockchain Network</SectionTitle>
            <SectionDescription>
              The blockchain network this policy applies to
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <ReadOnlyValue>
              <span className="capitalize">
                {getNetworkDisplayName(policy.networkId)}
              </span>
            </ReadOnlyValue>
          </SectionRightColumn>
        </SectionRow>
      </Section>

      {/* Policy type */}
      <Section title="Policy type">
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Policy type</SectionTitle>
            <SectionDescription>
              What happens to transactions that match this policy
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <ReadOnlyValue>{getPolicyTypeLabel(policy.type)}</ReadOnlyValue>
          </SectionRightColumn>
        </SectionRow>

        {/* Transaction approvers - only shown for manual approval policies */}
        {policy.type === 'MANUAL_APPROVAL' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Transaction approvers</SectionTitle>
                <SectionDescription>
                  Person or group that should approve transactions
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <ReadOnlyValue>
                  {policy.approverSetting ? (
                    policy.approverSetting.type === 'user' ? (
                      formatName(policy.approverSetting.userName ?? '')
                    ) : (
                      policy.approverSetting.groupName
                    )
                  ) : (
                    <span className="text-muted-foreground">
                      No approver selected
                    </span>
                  )}
                </ReadOnlyValue>
              </SectionRightColumn>
            </SectionRow>

            {/* Approval threshold - only shown for group approvers */}
            {policy.approverSetting?.type === 'group' && (
              <>
                <Separator className="w-full col-span-2" />
                <SectionRow>
                  <SectionLeftColumn>
                    <SectionTitle>Approval threshold</SectionTitle>
                    <SectionDescription>
                      Number of approvals required from the group
                    </SectionDescription>
                  </SectionLeftColumn>
                  <SectionRightColumn>
                    <ReadOnlyValue>
                      {policy.approverSetting.members?.length
                        ? `${policy.approverSetting.threshold} of ${policy.approverSetting.members.length} members`
                        : `${policy.approverSetting.threshold} approvals required`}
                    </ReadOnlyValue>
                  </SectionRightColumn>
                </SectionRow>
              </>
            )}
          </>
        )}
      </Section>

      {/* Transaction Conditions */}
      <Section title="Transaction conditions">
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Transaction source accounts</SectionTitle>
            <SectionDescription>
              Only apply policy to transactions from these accounts
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <ReadOnlyValue>
              {policy.accounts && policy.accounts.length > 0 ? (
                <div className="space-y-2">
                  {policy.accounts.map((account, index) => (
                    <div key={account.id}>
                      <AccountDisplay
                        account={account}
                        networkId={policy.networkId}
                      />
                      {index < policy.accounts.length - 1 && (
                        <div className="h-[1px] bg-border my-2"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">Any account</span>
              )}
            </ReadOnlyValue>
          </SectionRightColumn>
        </SectionRow>

        <Separator className="w-full col-span-2" />

        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Transaction initiator</SectionTitle>
            <SectionDescription>
              Only apply policy to transactions initiated by this person or
              group
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <ReadOnlyValue>
              {policy.initiatorSetting ? (
                policy.initiatorSetting.type === 'user' ? (
                  formatName(policy.initiatorSetting.userName ?? '')
                ) : (
                  policy.initiatorSetting.groupName
                )
              ) : (
                <span className="text-muted-foreground">
                  Any organization member
                </span>
              )}
            </ReadOnlyValue>
          </SectionRightColumn>
        </SectionRow>

        <Separator className="w-full col-span-2" />

        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Transaction type</SectionTitle>
            <SectionDescription>
              Only apply policy to transactions of this type
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <ReadOnlyValue>
              {getTransactionTypeLabel(policy.transactionType)}
            </ReadOnlyValue>
          </SectionRightColumn>
        </SectionRow>

        {/* Token Transfer Conditions */}
        {policy.transactionType === 'TOKEN_TRANSFER' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Token</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions sending this token
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <ReadOnlyValue>
                  {policy.tokenTransferCondition?.tokenSymbol ? (
                    <div className="flex items-center gap-2">
                      <TokenLogo
                        symbol={policy.tokenTransferCondition.tokenSymbol}
                        logoUrl={
                          policy.tokenTransferCondition.tokenLogoUrl ??
                          undefined
                        }
                        size="sm"
                      />
                      <span>{policy.tokenTransferCondition.tokenSymbol}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Any token</span>
                  )}
                </ReadOnlyValue>
              </SectionRightColumn>
            </SectionRow>

            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Amount threshold</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions with this amount
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <ReadOnlyValue>
                  {policy.tokenTransferCondition?.amountThreshold &&
                  policy.tokenTransferCondition?.tokenDecimals != null ? (
                    toHumanReadableTokenAmount(
                      policy.tokenTransferCondition.amountThreshold,
                      policy.tokenTransferCondition.tokenDecimals
                    )
                  ) : (
                    <span className="text-muted-foreground">No threshold</span>
                  )}
                </ReadOnlyValue>
              </SectionRightColumn>
            </SectionRow>

            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Transaction destination</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions sent to these addresses
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <ReadOnlyValue>
                  {renderAddresses(
                    policy.tokenTransferCondition,
                    policy.networkId
                  )}
                </ReadOnlyValue>
              </SectionRightColumn>
            </SectionRow>
          </>
        )}

        {/* Contract Interaction Conditions */}
        {policy.transactionType === 'CONTRACT_INTERACTION' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Contracts</SectionTitle>
                <SectionDescription>
                  Only apply policy to interactions with these contracts
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <ReadOnlyValue>
                  {contractInteractionCondition &&
                  contractInteractionCondition.length > 0 ? (
                    <div className="space-y-2">
                      {contractInteractionCondition.map((contract, index) => (
                        <div key={index} className="text-xs">
                          {(() => {
                            const proxyFunctions =
                              contract.proxyFunctions?.map((f) => f.name) || [];
                            const implementationFunctions =
                              contract.implementationFunctions?.map(
                                (f) => f.name
                              ) || [];

                            const hasFunctionRestriction =
                              proxyFunctions.length > 0 ||
                              implementationFunctions.length > 0;

                            const functionDetails = hasFunctionRestriction ? (
                              <>
                                Functions:{' '}
                                {proxyFunctions.map((fn, i) => (
                                  <span key={`proxy-${i}`}>
                                    {fn} (proxy)
                                    {i < proxyFunctions.length - 1 ||
                                    implementationFunctions.length > 0
                                      ? ', '
                                      : ''}
                                  </span>
                                ))}
                                {implementationFunctions.map((fn, i) => (
                                  <span key={`impl-${i}`}>
                                    {fn}
                                    {i < implementationFunctions.length - 1
                                      ? ', '
                                      : ''}
                                  </span>
                                ))}
                              </>
                            ) : (
                              'Any function'
                            );

                            return (
                              <ContractNameAddress
                                address={contract.contractAddress}
                                networkId={policy.networkId}
                                nameOverride={
                                  contractEntryByAddress.get(
                                    contract.contractAddress.toLowerCase()
                                  )?.name ?? null
                                }
                                details={functionDetails}
                              />
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Any contract</span>
                  )}
                </ReadOnlyValue>
              </SectionRightColumn>
            </SectionRow>
          </>
        )}

        {/* TODO: Function parameters support when backend implements it */}
        {/* {policy.transactionType === 'CONTRACT_INTERACTION' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Function parameters</SectionTitle>
                <SectionDescription>
                  Only apply policy to calls with these parameters
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <ReadOnlyValue>Any parameters</ReadOnlyValue>
              </SectionRightColumn>
            </SectionRow>
          </>
        )} */}
      </Section>

      {policy.transactionType === 'TOKEN_TRANSFER' ? (
        <Section title="Rate limits">
          <SectionRow>
            <SectionLeftColumn>
              <SectionTitle>Rate limits</SectionTitle>
              <SectionDescription>
                Control how often this policy can be used
              </SectionDescription>
            </SectionLeftColumn>
            <SectionRightColumn>
              <ReadOnlyValue>
                {renderLimitation(policy.limitation)}
              </ReadOnlyValue>
            </SectionRightColumn>
          </SectionRow>
          {policy.limitationUsage ? (
            <>
              <Separator className="w-full col-span-2" />
              <SectionRow>
                <SectionLeftColumn>
                  <SectionTitle>Current usage window</SectionTitle>
                  <SectionDescription>
                    {formatLocalizedDateTimeWithShortTz(
                      policy.limitationUsage.windowStart
                    )}
                    {' to '}
                    {formatLocalizedDateTimeWithShortTz(
                      policy.limitationUsage.windowEnd
                    )}
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionRightColumn>
                  {(() => {
                    const usageLimit = policy.limitationUsage;
                    const tokenSymbol =
                      policy.tokenTransferCondition?.tokenSymbol;
                    if (
                      nonZeroLimitationBuckets.length > 0 &&
                      usageLimit &&
                      tokenSymbol
                    ) {
                      const limitationWindowEnd =
                        usageLimit.windowEnd instanceof Date
                          ? usageLimit.windowEnd
                          : new Date(usageLimit.windowEnd);
                      return (
                        <div className="space-y-2 text-xs">
                          {nonZeroLimitationBuckets.map((bucket) => (
                            <div
                              key={JSON.stringify(bucket.key)}
                              className="rounded border p-2 space-y-2"
                            >
                              <div className="text-muted-foreground space-y-1">
                                {renderBucketDimension(
                                  'Initiator',
                                  bucket.key.initiatorKey,
                                  'initiator',
                                  bucket
                                )}
                                {renderBucketDimension(
                                  'Source',
                                  bucket.key.sourceAccountKey,
                                  'source',
                                  bucket
                                )}
                                {renderBucketDimension(
                                  'Destination',
                                  bucket.key.destinationKey,
                                  'destination',
                                  bucket
                                )}
                              </div>
                              <TokenTransferLimit
                                className="border-0 pt-0 mt-0 gap-1"
                                policyLimit={usageLimit.amountThreshold}
                                windowUsed={bucket.usedAmount}
                                currentTransactionAmount="0"
                                tokenSymbol={tokenSymbol}
                                windowResetAt={limitationWindowEnd}
                                showResetTimer={false}
                                copyVariant="windowUsage"
                              />
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <ReadOnlyValue>
                        <span className="text-muted-foreground">
                          No executed transfers in this window
                        </span>
                      </ReadOnlyValue>
                    );
                  })()}
                </SectionRightColumn>
              </SectionRow>
            </>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}
